import {Box, Divider, Stack} from '@mui/material';
import React, {Fragment} from 'react';

import {CTAButtonFixed, Snackbar, StyledCard, StyledGrid} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';

import {ServicesTaggingEnum} from 'main/analytics/gaTagsEnum';
import Icon from 'presentation/icons';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {translations} from 'presentation/translations-new';
import {formatText, sortByKey} from 'presentation/utils';
import {formatPrice, formatPriceIncomeTax} from 'presentation/utils/formatPrice';
import {CardEmpty} from '../../../components/CardEmpty';
import SearchEmptyBigSvg from '../../../components/CardEmpty/svgr/SearchEmptyBigSvg';
import {useIncomeTax} from '../../hooks/useIncomeTax';
import {DownloadIRCard} from './components/DownloadIRCard';
import {ZERO_TAX_VALUE} from './constants';
import {CardSubtitle, CardTitle, LineText, LineTitle, PriceLine, TotalPrice} from './styles';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {FeaturesEnum} from 'presentation/constants';

const {
	data,
	description,
	title,
	total,
	extract,
	contractNumber,
	beneficiaries,
	emptyCardTitle,
	emptyCardDescription,
	emptyDateCardDescription,
	nothing,
	baseYear,
} = translations['pt-br'].privateFlow.myFinancial.incomeTax;
const {errorDownloadPdf} = translations['pt-br'].privateFlow.myFinancial.coparticipation;
const {loading, pdfDownload} = translations['pt-br'].commons;

function IncomeTaxComponent() {
	const {isTablet} = useMediaQueries();
	const {
		SEARCH_PARAMS,
		incomeTaxData,
		isLoadingIncomeTaxData,
		isErrorIncomeTaxData,
		downloadPdf,
		isDownloading,
		isEmptyIncomeTaxData,
		isLoadingAvailableYears,
		isEmptyAvailableYears,
		downloadErrorState,
		handleToggleDownloadErrorState,
	} = useIncomeTax();
	const {
		beneficiary: {nuContratoC},
		accesses: {flSaoLucasSaude},
		beneficiariesLength,
	} = useAccountDataStore(store => store.accountData);

	const {isEnabled: isBlockIrSaoLucas} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_BLOCK_IR_SAO_LUCAS,
	);

	const {year} = SEARCH_PARAMS;
	const yearNumber = Number(year ?? 2000);

	const isBlockSaoLucas = isBlockIrSaoLucas && flSaoLucasSaude && yearNumber <= 2025;

	if (isLoadingIncomeTaxData || isLoadingAvailableYears) {
		return (
			<Stack alignItems="center" height="100%" justifyContent="center">
				<Loading text={loading} />
			</Stack>
		);
	}

	if (isEmptyAvailableYears && isEmptyIncomeTaxData) {
		return (
			<Box marginTop={5}>
				<CardEmpty title={emptyCardTitle} illustration={<SearchEmptyBigSvg />}>
					{emptyDateCardDescription}
				</CardEmpty>
			</Box>
		);
	}

	if (isErrorIncomeTaxData || isEmptyIncomeTaxData || isBlockSaoLucas) {
		return (
			<Box marginTop={5}>
				<CardEmpty title={emptyCardTitle} illustration={<SearchEmptyBigSvg />}>
					{emptyCardDescription}
				</CardEmpty>
			</Box>
		);
	}

	const {
		cdUsuario,
		dsEnderecoFontePagadora,
		dsFonteRecebedora,
		dsEnderecoFonteRecebedora,
		dsSituacaoUsuario,
		dtEmissao,
		listaValoresPorBeneficiarios,
		nuCnpjFonteRecebedora,
		nuValorTotalPago,
		nuCpfCnpjFontePagadora,
		nmSocial,
		nmFontePagadora,
	} = incomeTaxData?.impostoDeRenda[0] ?? {};

	const sortedBeneficiariesList = sortByKey(listaValoresPorBeneficiarios ?? [], 'nmBeneficiario');

	const isTotalValueString = typeof nuValorTotalPago === 'string';
	const isTaxAmountZero = isTotalValueString
		? nuValorTotalPago === ZERO_TAX_VALUE
		: nuValorTotalPago === 0;

	return (
		<Stack gap={2}>
			<Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
				<CardTitle>{title}</CardTitle>
				<CTAButtonFixed.Root>
					<CTAButtonFixed.Action
						variant={'outlined'}
						endIcon={
							isDownloading ? (
								<Loading color="inherit" size={16} />
							) : (
								<Icon name="download" size={16} />
							)
						}
						disabled={isDownloading}
						onClick={downloadPdf}
						data-tag-id={ServicesTaggingEnum.CLICK_BAIXAR_DEMOSTRATIVO_IR}>
						{pdfDownload}
					</CTAButtonFixed.Action>
				</CTAButtonFixed.Root>
			</Stack>
			{isTaxAmountZero ? (
				<DownloadIRCard />
			) : (
				<Fragment>
					<StyledCard.Container sx={{gap: 2}}>
						<CardSubtitle>{data?.data}</CardSubtitle>

						<Stack>
							<LineTitle>{description[1]}</LineTitle>
							<LineText>{dtEmissao}</LineText>
						</Stack>

						<Divider />

						<Stack direction="row" justifyContent="space-between">
							<Stack flex={1}>
								<LineTitle>{contractNumber}</LineTitle>
								<LineText>{nuContratoC}</LineText>
							</Stack>
							<Stack flex={1}>
								<LineTitle>{beneficiaries}</LineTitle>
								<LineText>{beneficiariesLength}</LineText>
							</Stack>
						</Stack>

						<Divider />

						<Stack>
							<LineTitle>{data?.receivingSource}</LineTitle>
							<LineText>{dsFonteRecebedora ?? nothing}</LineText>
							<LineText>{nuCnpjFonteRecebedora ?? nothing}</LineText>
							<LineText>{dsEnderecoFonteRecebedora ?? nothing}</LineText>
						</Stack>

						<Divider />

						<Stack>
							<LineTitle>{data?.payingSource}</LineTitle>
							<LineText>{nmSocial || nmFontePagadora || nothing}</LineText>
							<LineText>{nuCpfCnpjFontePagadora ?? nothing}</LineText>
							{nmSocial ? (
								<LineText>
									{data?.civilName}:{nmFontePagadora ?? nothing}
								</LineText>
							) : null}
						</Stack>

						<Divider />

						<StyledGrid.Root>
							<StyledGrid.Item lg={6}>
								<Stack>
									<LineTitle>{data?.userCode}</LineTitle>
									<LineText>{cdUsuario ?? nothing}</LineText>
								</Stack>
							</StyledGrid.Item>
							<StyledGrid.Item lg={6}>
								{isTablet ? <Divider /> : null}
								<Stack mt={isTablet ? 2 : 0}>
									<LineTitle>{data?.situation}</LineTitle>
									<LineText>{dsSituacaoUsuario ?? nothing}</LineText>
								</Stack>
							</StyledGrid.Item>
						</StyledGrid.Root>

						<Divider />

						<Stack>
							<LineTitle>{data?.address}</LineTitle>
							<LineText>{dsEnderecoFontePagadora ?? nothing}</LineText>
						</Stack>
					</StyledCard.Container>

					<StyledCard.Container>
						{listaValoresPorBeneficiarios && listaValoresPorBeneficiarios.length > 0 ? (
							<Fragment>
								<CardSubtitle>{extract}</CardSubtitle>

								<Stack gap={'16px'}>
									{sortedBeneficiariesList.map((beneficiario, index) => (
										<Stack key={index} gap={'16px'}>
											<PriceLine>
												<div>
													<LineTitle>
														{beneficiario?.nmSocialBeneficiario ||
															beneficiario?.nmBeneficiario}
													</LineTitle>
													<LineText>
														{formatText(
															beneficiario?.dsTipoBeneficiario,
														)}
													</LineText>
												</div>
												<LineText>
													{formatPrice(
														beneficiario?.nuVlNominalBeneficiarioFormatado,
													)}
												</LineText>
											</PriceLine>
											<Divider />
										</Stack>
									))}
								</Stack>
							</Fragment>
						) : null}

						<PriceLine>
							<div>
								<LineTitle>{total}</LineTitle>
								<LineText>
									{baseYear} {year}
								</LineText>
							</div>
							<TotalPrice>{formatPriceIncomeTax(nuValorTotalPago)}</TotalPrice>
						</PriceLine>
					</StyledCard.Container>
				</Fragment>
			)}
			<Snackbar
				message={errorDownloadPdf}
				onClose={handleToggleDownloadErrorState}
				open={downloadErrorState}
			/>
		</Stack>
	);
}

export {IncomeTaxComponent};
